// Listing deals (DECISIONS, 22 October): offers that apply by themselves, as
// in a shop window. "Buy 1, get 1 free" on the same item, or "Buy 2, get a
// phone case free" (another listing from the same shop, which the buyer adds
// to the cart too). The Worker prices them at checkout with dealDiscount; the
// app uses the same function to show them.

import { formatMoney } from './money.ts';

export type Deal =
  | { kind: 'bxgy'; buy: number; free: number }
  | { kind: 'gift'; buy: number; giftListingId: string; giftName: string; giftQuantity: number };

export type DealParse = { ok: true; value: Deal | null } | { ok: false; message: string };

const LISTING_ID = /^[A-Za-z0-9_-]{1,128}$/;

function whole(value: unknown, min: number, max: number): number | null {
  const number = Number(value);
  return Number.isInteger(number) && number >= min && number <= max ? number : null;
}

export function parseDeal(raw: unknown, listingId: string | null = null): DealParse {
  if (raw === null || raw === undefined || raw === '') return { ok: true, value: null };
  const input = (typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const buy = whole(input.buy, 1, 10);
  if (buy === null) return { ok: false, message: 'Buyers must buy between 1 and 10 to get the deal.' };
  if (input.kind === 'bxgy') {
    const free = whole(input.free, 1, 10);
    if (free === null) return { ok: false, message: 'Give between 1 and 10 free.' };
    return { ok: true, value: { kind: 'bxgy', buy, free } };
  }
  if (input.kind === 'gift') {
    const giftListingId = String(input.giftListingId || '');
    if (!LISTING_ID.test(giftListingId)) return { ok: false, message: 'Choose the item buyers get free.' };
    if (listingId && giftListingId === listingId) return { ok: false, message: 'For the same item free, use “Buy X, get Y free”.' };
    const giftQuantity = whole(input.giftQuantity ?? 1, 1, 5);
    if (giftQuantity === null) return { ok: false, message: 'Give between 1 and 5 of the free item.' };
    return { ok: true, value: { kind: 'gift', buy, giftListingId, giftName: String(input.giftName || '').trim().slice(0, 120) || 'a free item', giftQuantity } };
  }
  return { ok: false, message: 'Choose a deal.' };
}

export function dealLabel(deal: Deal): string {
  if (deal.kind === 'bxgy') return `Buy ${deal.buy}, get ${deal.free} free`;
  return `Buy ${deal.buy}, get ${deal.giftQuantity > 1 ? `${deal.giftQuantity} × ` : ''}${deal.giftName} free`;
}

export type DealLine = { listingId: string; unitMinor: number; quantity: number };
export type DealResult = { discountMinor: number; applied: { listingId: string; label: string; minor: number }[] };

// What one shop's deals take off: free units are the cheapest of that item.
export function priceDeals(lines: DealLine[], deals: ReadonlyMap<string, Deal>, maxDiscountMinor = Infinity): DealResult & { lines: DealLine[] } {
  const byListing = new Map<string, number>();
  // Keep actual unit prices: different versions/extras are separate buckets.
  // A unit already made free cannot be discounted a second time.
  const buckets = lines.map(line => ({ ...line }));
  for (const line of lines) {
    byListing.set(line.listingId, (byListing.get(line.listingId) ?? 0) + line.quantity);
  }
  const applied: DealResult['applied'] = [];
  const discounted: DealLine[] = [];
  let remaining = Math.max(0, maxDiscountMinor);
  for (const [listingId, quantity] of byListing) {
    const deal = deals.get(listingId);
    if (!deal) continue;
    let units: number;
    let target: string;
    if (deal.kind === 'bxgy') {
      const group = deal.buy + deal.free;
      units = Math.floor(quantity / group) * deal.free + Math.max(0, (quantity % group) - deal.buy);
      target = listingId;
    } else {
      units = Math.floor(quantity / deal.buy) * deal.giftQuantity;
      target = deal.giftListingId;
    }
    let minor = 0;
    for (const bucket of buckets.filter(line => line.listingId === target && line.unitMinor > 0).sort((a, b) => a.unitMinor - b.unitMinor)) {
      const count = Math.min(units, bucket.quantity, Math.floor(remaining / bucket.unitMinor));
      if (count > 0) {
        discounted.push({ listingId: target, unitMinor: 0, quantity: count });
        bucket.quantity -= count;
        units -= count;
        minor += count * bucket.unitMinor;
        remaining -= count * bucket.unitMinor;
      }
      // The shop's minimum may leave part of one unit payable. Keep that
      // residual as an integer price rather than rounding every unit.
      if (units > 0 && bucket.quantity > 0 && remaining > 0) {
        discounted.push({ listingId: target, unitMinor: bucket.unitMinor - remaining, quantity: 1 });
        bucket.quantity -= 1;
        units -= 1;
        minor += remaining;
        remaining = 0;
      }
    }
    if (minor > 0) applied.push({ listingId, label: dealLabel(deal), minor });
  }
  return { discountMinor: applied.reduce((sum, item) => sum + item.minor, 0), applied, lines: [...buckets.filter(line => line.quantity > 0), ...discounted] };
}

export function dealDiscount(lines: DealLine[], deals: ReadonlyMap<string, Deal>): DealResult {
  const { discountMinor, applied } = priceDeals(lines, deals);
  return { discountMinor, applied };
}

// "Flash sale · ends in 3 h 20 min" for sales ending within two days.
export function flashSaleText(saleEndsAt: string | null, now = Date.now()): string | null {
  const ends = Date.parse(String(saleEndsAt ?? ''));
  if (!Number.isFinite(ends) || ends <= now || ends - now > 48 * 60 * 60 * 1000) return null;
  const minutes = Math.max(1, Math.round((ends - now) / 60_000));
  const hours = Math.floor(minutes / 60);
  const left = hours ? `${hours} h${minutes % 60 ? ` ${minutes % 60} min` : ''}` : `${minutes} min`;
  return `Flash sale · ends in ${left}`;
}

export function dealSavingsText(result: DealResult): string {
  return result.applied.map(item => `${item.label} (−${formatMoney(item.minor)})`).join(' · ');
}
